// ─────────────────────────────────────────────
//  BenchAssignFlow — the quick-actions menu for ONE provider. Opened from the
//  OR Board's Unassigned list ("Assign"), and now also from a NAME on the
//  Timeline chart. Choices:
//
//    • New Room  → parent opens the simplified Add Assignment modal, CRNA
//                  pre-filled with this provider (onNewRoom).
//    • Takeover  → pick a room that already has a CRNA; this provider becomes
//                  the room's primary CRNA and the previous one is displaced.
//                  The displaced provider drops back to Unassigned and we ask
//                  "Home or Reassign" for them.
//    • Assign Supervising CRNA → pick somebody off today's roster to stand in
//                  THIS provider's room as the second provider, alongside them.
//                  Needs a room, so it only appears for a provider who has one.
//    • Edit CRNA → change when this person leaves the room they are standing
//                  in, and nothing else about them. ⚠️ THE ONLY OPTION THAT
//                  NEEDS NO STAFF ID: it writes the assignment row's SLOT
//                  (`ahpDepartTime` / `orienteeDepartTime`), addressed by which
//                  chair they sit in — so it is the last option standing on a
//                  row whose person genuinely cannot be identified.
//    • Give Breaks → they stay exactly where they are, on the bench, labelled
//                  GIVING BREAKS. The one placement that must NOT move anyone:
//                  the break-giver is still here and still callable, so an
//                  assignment row (which would hide them from the bench) or a
//                  departed flag (which would move them to Home) would both be
//                  the wrong shape. It is a label, and it toggles back off from
//                  this same modal — break rounds end.
//    • Home      → mark this provider departed for the day (moves to the Home
//                  section on the board).
//    • Transfer  → another campus; tracked in Traveling until Arrival.
//
//  ⚠️ WHICH CHOICES APPEAR IS DECIDED IN `domain/assignChoices`, NOT HERE. The
//  same five buttons cannot be right for a bench provider AND for somebody
//  standing in OR 9 ("they stay on the free list" is a lie told to the second
//  one), and five conditions spread through this JSX would drift the first time
//  a sixth option landed.
//
//  ⚠️ IT IS A MENU, and it is opened from a control inside a case bar that is
//  itself a button. Nested interactive elements are invalid HTML, so the TRIGGER
//  must be a sibling of the bar's own button, not a child of it — see the props
//  block below for what the caller owes this component.
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../context/ToastContext";
import {
  updateAssignmentRow,
  setRoomAssignment,
  setRosterMemberDeparted,
  setRosterMemberGivingBreaks,
  createTransfer,
} from "../firebase/firestore";
import { useFacilities } from "../hooks/useFacilities";
import { resolveFacilityStatus } from "../domain/facilityRules";
import { facilityLabel } from "../domain/facilities";
import { assignChoices, BENCH, SEAT, SECOND } from "../domain/assignChoices";
import { secondProviderOf, SECOND_PROVIDER_ROLES } from "../domain/secondProvider";
import {
  setSecondProviderPatch,
  clearSecondProviderPatch,
  setDepartPatch,
  recordedDepartTime,
  isResolvedProvider,
  seatCellName,
  asAssignmentRow,
  matchesSecondProvider,
} from "../domain/secondProviderWrite";
import { to12h, hmToMinutes, rowClaimsAt } from "../domain/timeline";
import st from "./benchAssign/benchAssignStyles";
import ChoiceMenu from "./benchAssign/ChoiceMenu";
import AssignSteps from "./benchAssign/AssignSteps";
import optionCopy, { identityNote } from "./benchAssign/optionCopy";
import writeGuard from "./benchAssign/writeGuard";

// Match an assignment row's orRoom back to a live room doc (orRoom drops the
// "OR " prefix the way createAssignment stores it).
const roomForRow = (rooms, row) =>
  rooms.find((r) => r.name === row.orRoom || r.name.replace(/^OR\s+/i, "") === row.orRoom) || null;

export default function BenchAssignFlow({
  provider,
  rooms = [],
  assignments = [],
  facilityId,
  date,
  updatedBy = null,
  // Today's CRNA/AA roster — the pool "Assign Supervising CRNA" picks from.
  // Empty (the default) simply hides that option, so a caller that has not
  // wired it up yet loses nothing it had.
  crnaPool = [],
  // The assignment row this provider is currently running, when the caller
  // already knows it (the timeline does — the menu opened from that very bar).
  // Left null, it is derived from `assignments` by `ahpId`.
  currentRow = null,
  // The wall clock, so a row that no longer CLAIMS this provider stops counting
  // as their seat. Optional and defaulting to null, which reproduces the old
  // timeless behaviour exactly — a caller that has not been wired up loses
  // nothing it had.
  nowMin = null,
  // Where keyboard focus goes when the menu closes. Defaults to whatever had
  // focus when it opened, which is the trigger in every real caller.
  returnFocusRef = null,
  onNewRoom,
  onClose,
}) {
  const toast = useToast();
  // choose | takeover | second_crna | edit_depart | home_reassign | transfer
  const [step, setStep] = useState("choose");
  const [displaced, setDisplaced] = useState(null); // { id, name } after a takeover
  const [busy, setBusy] = useState(false);
  const menuRef = useRef(null);
  const openerRef = useRef(null);
  // Transfer destinations: every OTHER active facility.
  const allFacilities = useFacilities();
  const transferTargets = allFacilities.filter(
    (f) => f.id !== facilityId && resolveFacilityStatus(f) === "active"
  );
  const labelOf = (f) => {
    const known = facilityLabel(f.id);
    return known !== f.id ? known : f.label || f.name || f.id;
  };

  // Every id-keyed write below is guarded INDEPENDENTLY of the menu that offered
  // it — `benchAssign/writeGuard` says why hiding the option is not enough.
  const canWriteAs = writeGuard(toast);

  // ── FOCUS, ON OPEN AND ON CLOSE ─────────────────────────────────────────
  // ⚠️ THIS COMPONENT STAYS MOUNTED between opens (the parent renders it with
  // provider=null), so "open" is a provider arriving, not a mount. Effects have
  // to sit above the `if (!provider) return null` guard or the hook order
  // changes between renders.
  // ⚠️ `key` IS PART OF THE IDENTITY NOW. A name nobody could identify has no
  // `id` at all, so keying this effect on the id alone would leave the menu open
  // with nothing focused and an arrow key with nowhere to start.
  const providerKey = provider?.id || provider?.key || provider?.name || null;
  useEffect(() => {
    if (!providerKey) return undefined;
    openerRef.current =
      (returnFocusRef && returnFocusRef.current) ||
      (typeof document !== "undefined" ? document.activeElement : null);
    // Focus the first item so the arrow keys have somewhere to start, and so a
    // keyboard user is not left behind on a trigger the overlay now covers.
    const t = setTimeout(() => {
      const first = menuRef.current?.querySelector('[role="menuitem"]:not([disabled])');
      if (first) first.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [providerKey, returnFocusRef]);

  const doTransfer = async (dest) => {
    if (!canWriteAs(provider?.id)) return;
    setBusy(true);
    try {
      await createTransfer({
        providerId: provider.id,
        providerName: provider.name,
        subRole: provider.subRole || "crna",
        fromFacilityId: facilityId,
        toFacilityId: dest.id,
        toFacilityName: labelOf(dest),
        date,
        createdBy: updatedBy,
      });
      // They've left this building — origin roster marks them departed; the
      // Traveling card tracks them until Arrival at the destination.
      await setRosterMemberDeparted(facilityId, date, provider.id, true, updatedBy);
      toast.success(`${provider.name} is traveling to ${labelOf(dest)} — Arrival confirms when they get there.`);
      close();
    } catch (e) {
      console.error("Transfer failed:", e);
      toast.error("Could not start the transfer.");
      setBusy(false);
    }
  };

  if (!provider) return null;

  // FULL reset on close — this component stays MOUNTED between opens (parent
  // renders it with provider=null), so any state left behind leaks into the
  // next open. `busy` stuck true after a successful transfer was exactly the
  // "second transfer: buttons won't click" bug.
  //
  // Focus goes back to whatever opened the menu, deferred one tick so React has
  // torn the overlay down first — focusing an element the browser still
  // considers covered is how focus ends up on <body>.
  const close = () => {
    setStep("choose"); setDisplaced(null); setBusy(false);
    const back = openerRef.current;
    openerRef.current = null;
    onClose();
    if (back && typeof back.focus === "function") setTimeout(() => back.focus(), 0);
  };

  // Toggle, not a one-way door: the SAME button reads "Stop giving breaks" once
  // the label is on, so the AIC who set it can clear it without a database edit.
  // `provider.givingBreaks` rides in on the bench entry (benchProviders), so
  // this never re-derives the fact it is about to invert.
  const givingBreaks = Boolean(provider?.givingBreaks);

  const toggleGivingBreaks = async () => {
    if (!canWriteAs(provider.id)) return;
    setBusy(true);
    try {
      await setRosterMemberGivingBreaks(facilityId, date, provider.id, !givingBreaks, updatedBy);
      toast.success(
        givingBreaks
          ? `${provider.name} is no longer giving breaks.`
          : `${provider.name} is giving breaks — still on the free list.`
      );
      close();
    } catch (e) {
      console.error("Giving-breaks toggle failed:", e);
      toast.error("Could not update the break-giver label.");
      setBusy(false);
    }
  };

  const sendHome = async (id, name) => {
    if (!canWriteAs(id)) return;
    setBusy(true);
    try {
      // updatedBy must be a real value — Firestore rejects undefined fields
      // (no ignoreUndefinedProperties), which would silently fail the write.
      await setRosterMemberDeparted(facilityId, date, id, true, updatedBy);
      toast.success(`${name} sent home for the day.`);
      close();
    } catch (e) {
      console.error("Send home failed:", e);
      toast.error("Could not send them home.");
      setBusy(false);
    }
  };

  // Rooms that currently have a CRNA assigned (excluding this provider).
  const occupiedRows = assignments
    .filter((a) => a.ahpId && a.ahpId !== provider.id && a.ahpName)
    .slice()
    .sort((a, b) => (a.orRoom || "").localeCompare(b.orRoom || "", undefined, { numeric: true }));

  // ── THE ROOM THIS PROVIDER IS RUNNING ───────────────────────────────────
  // Given by the caller when it knows (the timeline opened this menu from that
  // bar), otherwise found by the same key `benchProviders` matches on.
  // ⚠️ EITHER SHAPE. The board hands raw assignment rows; the timeline hands a
  // `buildTimeline` case, which carries the second person on `case.orientee`
  // rather than on the `orientee*` fields the resolver reads. `asAssignmentRow`
  // reshapes it — it does not re-derive anything.
  // ⚠️ THE SAME QUESTION THE BENCH ASKED, NOT A SECOND ONE. `benchProviders`
  // skips rows where `!rowClaimsAt(a, nowMin)` — "a finished case releases
  // everyone on it" — so a provider whose case has ended is on the bench. This
  // derivation had no clock at all, so it matched a row that no longer claims
  // them and then drove EVERY branch in this file from it: `position` became
  // SEAT instead of BENCH, and the menu offered an AIC "put a second CRNA in
  // WOR 12 alongside them" and "set when they leave WOR 12" for a room whose
  // case finished hours earlier. Seen live on the Wesley Chapel board.
  //
  // `currentRow` is still trusted outright: the timeline hands it in because
  // the menu was opened FROM that case's bar, and the caller knows better than
  // the clock there.
  const myRow = currentRow
    ? asAssignmentRow(currentRow)
    : assignments.find(
        (a) =>
          a.ahpId &&
          a.ahpId === provider.id &&
          (!Number.isFinite(nowMin) || rowClaimsAt(a, nowMin))
      ) || null;
  const existingSecond = myRow ? secondProviderOf(myRow) : null;

  // ⚠️ AM I THE SECOND PROVIDER, OR THE ONE WHO HOLDS THE ROOM? A paired bar has
  // TWO names and the menu opens from either. Without this the second name
  // resolved `existingSecond` to HERSELF, so "Assign Supervising CRNA" read
  // "Swap Kayla Gibbons for somebody else beside Kayla Gibbons" — one string
  // covering two opposite intentions, whose write replaced the very person the
  // operator had clicked on.
  //
  // Compared on `key` as well as `id`: a second provider whose name resolved to
  // no staff record carries the synthetic `sp:` key that `secondProviderMember`
  // hands the roster, and that is the id the chart passes back in. The name is
  // the last resort, for the same reason a missing id is not an absent person.
  const iAmSecond = matchesSecondProvider(provider, existingSecond);
  const position = !myRow ? BENCH : iAmSecond ? SECOND : SEAT;

  // ⚠️ DID ANYBODY ACTUALLY IDENTIFY THIS PERSON? Every option except "Edit
  // CRNA" writes a document keyed by a provider id. NOT `Boolean(provider.id)`:
  // `secondProviderMember` mints a truthy synthetic `sp:<name>`. The chart has
  // already tried the name against the staff pool (`resolveBarIdentity`) by
  // now, so what reaches here unidentified is genuinely unidentifiable.
  const identified = isResolvedProvider(provider);

  // Everybody on today's roster except the people already standing in that room.
  const inRoomIds = new Set(
    [provider.id, provider.key, myRow?.ahpId, existingSecond?.id, existingSecond?.key].filter(Boolean)
  );
  const secondCandidates = position === SEAT ? crnaPool.filter((p) => p && p.id && !inRoomIds.has(p.id)) : [];

  // ── THE LEAVING TIME THIS ROW RECORDS ───────────────────────────────────
  // ⚠️ THE STORED ROW, NOT THE TIMELINE CASE. A `buildTimeline` case publishes
  // `departMin`/`depart12h`, which on most rows are DERIVED from a shift code or
  // from today's roster; it does not carry `ahpDepartTime`/`orienteeDepartTime`
  // at all. Seeding the editor from the derived minute would offer a borrowed
  // number as if a human had typed it — and save it as one on the next press.
  const storedRow = (myRow && assignments.find((a) => a && a.id === myRow.id)) || myRow;
  const departTime = recordedDepartTime(storedRow, { second: position === SECOND });
  const departLabel = departTime ? to12h(hmToMinutes(departTime)) || departTime : "";

  const choices = assignChoices({
    position,
    identified,
    givingBreaks,
    takeoverTargets: occupiedRows.length,
    secondCrnaCandidates: secondCandidates.length,
    transferTargets: transferTargets.length,
  });

  // A raw row names the room on `orRoom` ("9"); a timeline case names it on
  // `roomName` ("WOR 9"), already prefixed.
  const rawRoom = myRow?.orRoom || myRow?.roomName || "";
  const roomLabel = rawRoom
    ? (/^[a-z]/i.test(rawRoom) ? String(rawRoom) : `OR ${rawRoom}`)
    : "the room";
  // Who keeps the room when the second provider steps out of it.
  // ⚠️ `seatCellName`, NOT `ahpName`. On a legacy row the raw cell is still the
  // whole sentence "Trevor Stamand 5 orient Kayla Gibbons", and this string is
  // printed straight back at the operator — the same trap the takeover path
  // carries a warning about.
  const seatLabel = (myRow ? seatCellName(myRow) || myRow.ahpName : "") || "The other CRNA";

  const assignSecondCrna = async (person) => {
    // ⚠️ THE OPTION IS ALREADY HIDDEN OUTSIDE the seat position, but the guard
    // is on the WRITE as well: a menu that offers the wrong thing is a copy bug,
    // a write that does the opposite of what the operator clicked is a patient
    // -safety one, and only one of those two is allowed to depend on the JSX
    // above staying correct.
    if (!myRow || position !== SEAT) return;
    setBusy(true);
    try {
      // ⚠️ NO ROOM-DOC MIRROR. `room.providerId`/`providerName` is one field pair
      // describing who HOLDS the room, and the second provider does not —
      // writing them here would tell RoomCard that the helper is running it and
      // the seat holder has gone.
      await updateAssignmentRow(
        myRow.id,
        setSecondProviderPatch(myRow, person, { role: SECOND_PROVIDER_ROLES.HELPING })
      );
      toast.success(`${person.name} is with ${provider.name} in ${roomLabel}.`);
      close();
    } catch (e) {
      console.error("Assign supervising CRNA failed:", e);
      toast.error("Could not add the second CRNA.");
      setBusy(false);
    }
  };

  // ── UNASSIGNED → out of the room, still on today's roster ───────────────
  //
  // Owner, 2026-09-09: *"Can you add unassigned"*, with this menu open on the
  // second CRNA in WOR 9. Nothing here moved a person OUT of a room and left
  // them available; every option either sent them somewhere else or edited a
  // fact about them.
  //
  // ⚠️ THE SAME WRITE `TimelineCaseModal.removeSecond` ALREADY MAKES, asked of
  // the same helper. `clearSecondProviderPatch` empties the second-provider
  // slot and nothing else: the seat still holds the room, `secondProviderRole`
  // goes with it (they are not orienting anybody any more), and the person
  // reappears on FREE NOW because `benchProviders` is roster-minus-assigned —
  // there is no second write naming them, and there must not be one, or "free"
  // acquires a second definition.
  //
  // ⚠️ NO ROOM-DOC MIRROR, and that asymmetry is deliberate. `room.providerId` /
  // `providerName` is one field pair describing who HOLDS the room; the second
  // provider never did, so touching it here would tell RoomCard the seat holder
  // has gone. Every SEAT write in this app must move the row and the room doc
  // together; this one must not.
  //
  // ⚠️ GUARDED AT THE WRITE, not only in the menu — `assignChoices` already
  // hides it outside SECOND, and `assignSecondCrna` carries the same belt and
  // braces for the same reason: a menu offering the wrong option is a copy bug,
  // a write emptying the wrong chair is a patient-safety one.
  const unassignSecond = async () => {
    if (!myRow || position !== SECOND) return;
    setBusy(true);
    try {
      await updateAssignmentRow(myRow.id, clearSecondProviderPatch(myRow));
      toast.success(
        `${provider.name} is out of ${roomLabel}. ${seatLabel} still has the room.`
      );
      close();
    } catch (e) {
      console.error("Unassign second CRNA failed:", e);
      toast.error("Could not take them out of the room.");
      setBusy(false);
    }
  };

  // ── EDIT CRNA → the leaving time, and ONLY the leaving time ─────────────
  //
  // ⚠️ GUARDED INDEPENDENTLY OF THE RENDER, exactly as `assignSecondCrna` is,
  // and for the same reason: a menu that offers the wrong option is a copy bug,
  // a write that touches the wrong person's slot is a patient-safety one. The
  // slot is re-derived from `position` here rather than trusted from the button
  // that was pressed.
  //
  // ⚠️ AND IT IS A ONE-FIELD PATCH. Writing the seat's or the second provider's
  // full field set on a TIME edit would re-flatten a legacy two-person cell, or
  // relabel a colleague helping out as somebody's trainee — the mistake
  // `setSecondDepartPatch` already exists to avoid on the timeline drag.
  const saveDepart = async (nextTime) => {
    if (!myRow || !myRow.id || position === BENCH) return;
    const second = position === SECOND;
    setBusy(true);
    try {
      await updateAssignmentRow(myRow.id, setDepartPatch({ second, departTime: nextTime }));
      const spoken = nextTime ? to12h(hmToMinutes(nextTime)) || nextTime : "";
      toast.success(
        spoken
          ? `${provider.name} now leaves ${roomLabel} at ${spoken}.`
          : `${provider.name} has no recorded leaving time in ${roomLabel}.`
      );
      close();
    } catch (e) {
      console.error("Leaving-time edit failed:", e);
      toast.error("Could not save that leaving time.");
      setBusy(false);
    }
  };

  const doTakeover = async (row) => {
    if (!canWriteAs(provider.id)) return;
    setBusy(true);
    try {
      const room = roomForRow(rooms, row);
      // ⚠️ SHE MAY ALREADY BE IN THIS ROOM. "Trevor goes home, Kayla takes WOR 9"
      // is the ordinary end of an orienting pair, and it is reached from HER
      // name — but writing only the seat fields would leave one person named
      // twice on one row, as the seat AND as the second provider. Clearing comes
      // FIRST so the seat fields below still win on `ahpName`; that ordering is
      // load-bearing, because `clearSecondProviderPatch` also normalises a
      // legacy mashed cell and would otherwise overwrite the new seat name.
      const takingOverMyOwnRoom = iAmSecond && myRow && row.id === myRow.id;
      // Swap the room's primary CRNA to this provider; their scheduled leave
      // (shiftCode) travels with them so the room still tracks the real end time.
      await updateAssignmentRow(row.id, {
        ...(takingOverMyOwnRoom ? clearSecondProviderPatch(row) : null),
        ahpId: provider.id,
        ahpName: provider.name,
        ahpShiftCode: provider.shiftCode ?? null,
        ahpDepartTime: "",
      });
      if (room) {
        await setRoomAssignment(room.id, {
          providerId: provider.id,
          providerName: provider.name,
          providerRole: (provider.subRole || "crna").toUpperCase(),
        });
      }
      // The previous CRNA is now unassigned — ask what's next for them.
      // ⚠️ THE SEAT, NOT THE RAW CELL. On a legacy row `ahpName` is still
      // "Trevor Stamand 5 orient Kayla Gibbons", and this string is printed
      // straight back at the operator as "<name> was displaced".
      setDisplaced({ id: row.ahpId, name: seatCellName(row) || row.ahpName });
      setBusy(false);
      setStep("home_reassign");
      toast.success(`${provider.name} took over ${row.orRoom}.`);
    } catch (e) {
      console.error("Takeover failed:", e);
      toast.error("Could not complete the takeover.");
      setBusy(false);
    }
  };

  // ── THE OPTION TABLE ────────────────────────────────────────────────────
  // WHICH of these appear is `domain/assignChoices`; WHAT each one says is
  // `benchAssign/optionCopy`; what each one DOES is the handler passed in here.
  // Split that way because the first is a rule with three positions and a test,
  // and the second is copy — and the bug that made this file worth re-reading
  // was a single string trying to be both.
  const OPTIONS = optionCopy({
    provider,
    roomLabel,
    givingBreaks,
    position,
    existingSecond,
    seatName: seatLabel,
    departLabel,
    actions: {
      onNewRoom: () => { onNewRoom(provider.id); },
      onTakeover: () => setStep("takeover"),
      onSecondCrna: () => setStep("second_crna"),
      onEditDepart: () => setStep("edit_depart"),
      onUnassign: unassignSecond,
      onToggleBreaks: toggleGivingBreaks,
      onHome: () => sendHome(provider.id, provider.name),
      onTransfer: () => setStep("transfer"),
    },
  });
  const choiceItems = choices.map((id) => ({ key: id, ...OPTIONS[id] }));
  const noteText = identified ? "" : identityNote({ provider, roomLabel });

  // ── KEYBOARD ────────────────────────────────────────────────────────────
  // Escape closes (and focus goes back to the trigger, in `close`); the arrows,
  // Home and End move between the options. Enter/Space are free — every option
  // is a real <button>, which is also why the trigger must NOT be nested inside
  // the case bar's own button.
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = [...(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || [])];
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement);
    const next =
      e.key === "Home" ? 0
      : e.key === "End" ? items.length - 1
      : e.key === "ArrowDown" ? (at + 1 + items.length) % items.length
      : (at - 1 + items.length) % items.length;
    items[next].focus();
  };

  // PORTAL to <body> (same as Modal/SoloReliefFlow/DoctorAssignFlow): escapes
  // the TV board's transform:scale, which otherwise re-anchors this fixed
  // overlay and makes the options unclickable on real TVs.
  return createPortal(
    <div style={st.overlay} role="dialog" aria-modal="true" aria-label={`Assign ${provider.name}`}>
      {/* The panel of a role=dialog that owns its own keyboard handling —
          onKeyDown is what makes this reachable, not what makes it unreachable. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div style={st.modal} ref={menuRef} onKeyDown={onKeyDown}>
        {step === "choose" && (
          <>
            <div style={st.title}>Assign {provider.name}</div>
            <div style={st.sub}>{(provider.subRole || "crna").toUpperCase()} · choose how to place them.</div>
            {/* ⚠️ THE REFUSAL IS VISIBLE, OR IT IS A DEFECT REPORT: a menu quietly
                showing one option where the room next door shows five reads as
                broken, which is precisely what the owner filed. "" for everyone
                the chart could identify, which is now nearly everybody. */}
            {noteText && <div role="note" style={st.note}>{noteText}</div>}
            <ChoiceMenu label={`Placement options for ${provider.name}`} items={choiceItems} busy={busy} />
            <div style={st.actions}>
              <button type="button" style={st.ghost} onClick={close} disabled={busy}>Cancel</button>
            </div>
          </>
        )}

        {/* Every screen after the first one. Which of them can be REACHED is
            decided by `choices` above; each one's write is a callback this file
            owns, so the guard stays next to the position it guards. */}
        <AssignSteps
          step={step}
          provider={provider}
          roomLabel={roomLabel}
          busy={busy}
          occupiedRows={occupiedRows}
          onTakeover={doTakeover}
          existingSecond={existingSecond}
          secondCandidates={secondCandidates}
          onAssignSecond={assignSecondCrna}
          departTime={departTime}
          onSaveDepart={saveDepart}
          transferTargets={transferTargets}
          labelOf={labelOf}
          onTransfer={doTransfer}
          displaced={displaced}
          onSendHome={sendHome}
          onBack={() => setStep("choose")}
          onCancel={close}
        />
      </div>
    </div>,
    document.body
  );
}
